import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Building2, CheckCircle, Clock, XCircle, Plus, Search, Upload, AlertCircle } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { BackButton } from '../components/BackButton';
import { CriarDocumentoFiscalInput, prestacoesApi, unidadesApi } from '../api/backend';
import { useAuthStore } from '../store/authStore';
import type { DocumentoFiscal, PrestacaoContas, Unidade } from '../types';
import { useToast } from '../hooks/useToast';
import { formatCurrencyWithSymbol } from '../helpers/format-money';

const VALOR_LIQUIDO = 'Valor Título Liquido'
const VALOR_ORIGINAL = 'Valor Título Original'

const NOME_FILIAL = 'DESCRICAOFILIAL'
const CODIGO_FILIAL = 'CODFILIAL'


export function PrestacoesPage() {
  const [prestacoes, setPrestacoes] = useState<PrestacaoContas[]>([]);
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
  const [filtroUnidade, setFiltroUnidade] = useState<number | undefined>();
  const [filtroBusca, setFiltroBusca] = useState('');
  const { selectedUnidadeId, user, isOperator, isAdminRole } = useAuthStore();
  const toast = useToast();

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Se o usuário selecionou uma unidade específica, filtrar automaticamente
    if (selectedUnidadeId !== null && selectedUnidadeId !== undefined) {
      setFiltroUnidade(selectedUnidadeId);
    }
  }, [selectedUnidadeId]);

  useEffect(() => {
    loadData();
  }, [filtroAno, filtroUnidade]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Se o usuário está logado como cliente de uma unidade específica,
      // forçar o filtro para aquela unidade
      const unidadeParaFiltrar = selectedUnidadeId !== null && selectedUnidadeId !== undefined 
        ? selectedUnidadeId 
        : filtroUnidade;
      
      const [prestacoesData, unidadesData] = await Promise.all([
        prestacoesApi.list(filtroAno, unidadeParaFiltrar),
        unidadesApi.list(),
      ]);
      console.log([prestacoesData, unidadesData]);
      setPrestacoes(prestacoesData);
      setUnidades(unidadesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMesNome = (mes: number) => {
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return meses[mes - 1];
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      aberto: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      fechado: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    };
    return styles[status as keyof typeof styles] || styles.aberto;
  };

  type PrestacaoAprovacaoStatus = NonNullable<PrestacaoContas['status_aprovacao']> | 'desconhecido';

  const prestacaoAprovacaoConfig: Record<PrestacaoAprovacaoStatus, { label: string; classes: string; Icon: typeof Clock }> = {
    submetida: {
      label: 'Em análise',
      classes: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
      Icon: Clock,
    },
    deferido: {
      label: 'Análise aprovada',
      classes: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
      Icon: CheckCircle,
    },
    indeferido: {
      label: 'Análise indeferida',
      classes: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
      Icon: XCircle,
    },
    nao_submetida: {
      label: 'Não submetida',
      classes: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-200',
      Icon: AlertCircle,
    },
    desconhecido: {
      label: 'Status desconhecido',
      classes: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-200',
      Icon: AlertCircle,
    },
  };

  const renderPrestacaoAprovacaoBadge = (status?: PrestacaoContas['status_aprovacao']) => {
    const normalized = (status ?? 'nao_submetida') as PrestacaoAprovacaoStatus;
    const config = prestacaoAprovacaoConfig[normalized] || prestacaoAprovacaoConfig.desconhecido;
    const Icon = config.Icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.classes}`}>
        <Icon className="w-3.5 h-3.5" />
        <span className="capitalize">{config.label}</span>
      </span>
    );
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const XLSX = await import('xlsx');
        const reader = new FileReader();
        reader.onload = async (e) => {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet) as any;

          // Converter datas do Excel (serial numbers) para objetos Date
          const convertExcelDate = (serial: number) => {
            return new Date((serial - 25569) * 86400 * 1000);
          };

          jsonData.forEach((row: any) => {
            if (typeof row.DATAEMISSAO === 'number') {
              row.DATAEMISSAO = convertExcelDate(row.DATAEMISSAO);
            }
            if (typeof row.DATALANCAMENTO === 'number') {
              row.DATALANCAMENTO = convertExcelDate(row.DATALANCAMENTO);
            }
          });

          console.log('Dados da planilha:', jsonData);

          // Validação
          let errors: any = [];

          jsonData.forEach((item: any) => {

          });

          if (errors.length > 0) {
            console.error('Erros encontrados na planilha:', errors);
            toast.error(`Foram encontrados ${errors.length} erros na planilha. Por favor, revise os dados e tente novamente.`);
            return;
          }

          // Upsert unidades
          const unidadesPorCodigo = new Map(
            unidades
              .filter((unidade) => unidade.codigo)
              .map((unidade) => [unidade.codigo.trim(), unidade] as const)
          );

          const codigosPlanilha = new Map<string, string>();

          jsonData.forEach((item: Record<string, any>) => {
            const codigo = item[CODIGO_FILIAL];
            if (!codigo && codigo !== 0) return;
            const codigoNormalizado = String(codigo).trim();
            if (!codigoNormalizado || unidadesPorCodigo.has(codigoNormalizado)) return;

            const nome = item[NOME_FILIAL];
            const nomeNormalizado = typeof nome === 'string' && nome.trim().length > 0
              ? nome.trim()
              : codigoNormalizado;
            codigosPlanilha.set(codigoNormalizado, nomeNormalizado);
          });

          if (codigosPlanilha.size > 0) {
            const novasUnidades: Unidade[] = [];
            for (const [codigo, nome] of codigosPlanilha.entries()) {
              try {
                const created = await unidadesApi.create({
                  nome,
                  codigo,
                  cnpj: null,
                  responsavel_nome: user?.display_name || user?.nome || 'Responsável não informado',
                  responsavel_email: user?.email || '',
                  responsavel_telefone: null,
                  responsavel_cargo: null,
                  responsavel_cpf: null,
                });
                unidadesPorCodigo.set(codigo, created);
                novasUnidades.push(created);
                toast.success('Unidade criada com sucesso', `A unidade ${created.nome || codigo} foi adicionada.`);
              } catch (createError) {
                console.error('Erro ao criar unidade a partir da planilha:', createError);
              }
            }

            if (novasUnidades.length) {
              setUnidades((prev) => [...prev, ...novasUnidades]);
            }
          }

          // Upsert prestações

          const prestacoesPorChave = new Map<string, PrestacaoContas>(
            prestacoes.map((prestacao) => [`${prestacao.unidade_id}-${prestacao.ano}-${prestacao.mes}`, prestacao] as const)
          );

          const prestacoesFetchCache = new Map<string, Promise<PrestacaoContas[]>>();
          const prestacoesCreateCache = new Map<string, Promise<PrestacaoContas>>();

          const getPrestacaoKey = (unidadeId: number, ano: number, mes: number) => `${unidadeId}-${ano}-${mes}`;

          const resolveDataReferencia = (item: Record<string, any>) => {
            const candidato = item.DATALANCAMENTO || item.DATAEMISSAO;
            if (!candidato) return null;
            if (candidato instanceof Date) return candidato;
            const parsed = new Date(candidato);
            return isNaN(parsed.getTime()) ? null : parsed;
          };

          const ensurePrestacao = async (unidade: Unidade, ano: number, mes: number) => {
            const chave = getPrestacaoKey(unidade.id, ano, mes);
            if (prestacoesPorChave.has(chave)) {
              return prestacoesPorChave.get(chave)!;
            }

            const fetchKey = `${unidade.id}-${ano}`;
            if (!prestacoesFetchCache.has(fetchKey)) {
              prestacoesFetchCache.set(fetchKey, prestacoesApi.list(ano, unidade.id));
            }

            const registros = await prestacoesFetchCache.get(fetchKey)!;
            registros.forEach((prestacao) => {
              prestacoesPorChave.set(getPrestacaoKey(prestacao.unidade_id, prestacao.ano, prestacao.mes), prestacao);
            });

            if (prestacoesPorChave.has(chave)) {
              return prestacoesPorChave.get(chave)!;
            }

            if (!prestacoesCreateCache.has(chave)) {
              prestacoesCreateCache.set(
                chave,
                prestacoesApi.create({
                  unidade_id: unidade.id,
                  ano,
                  mes,
                  responsavel_nome: user?.display_name || user?.nome || 'Responsável não informado',
                  responsavel_cpf: user?.cpf || '00000000000',
                })
              );
            }

            const criada = await prestacoesCreateCache.get(chave)!;
            prestacoesPorChave.set(chave, criada);
            setPrestacoes((prev) => (prev.find((p) => p.id === criada.id) ? prev : [...prev, criada]));
            toast.success('Prestação criada', `Criada prestação ${getMesNome(mes)}/${ano} para ${unidade.nome || unidade.codigo || unidade.id}.`);
            return criada;
          };

          const prestacaoTasks: Promise<any>[] = [];

          jsonData.forEach((item: Record<string, any>) => {
            const codigo = item[CODIGO_FILIAL];
            if (!codigo && codigo !== 0) return;

            const unidade = unidadesPorCodigo.get(String(codigo).trim());
            if (!unidade) return;

            const dataReferencia = resolveDataReferencia(item);
            if (!dataReferencia) return;

            const ano = dataReferencia.getFullYear();
            const mes = dataReferencia.getMonth() + 1;
            if (!ano || !mes) return;

            prestacaoTasks.push(
              ensurePrestacao(unidade, ano, mes).catch((error) => {
                console.error('Erro ao garantir prestação da planilha:', error);
                toast.error('Erro ao criar prestação automaticamente. Revise os dados e tente novamente.');
              })
            );
          });

          await Promise.all(prestacaoTasks);

          const documentosFetchCache = new Map<number, Promise<DocumentoFiscal[]>>();
          const documentoNumeroSetCache = new Map<number, Set<string>>();

          const ensureDocumentoNumeroSet = async (prestacaoId: number) => {
            if (documentoNumeroSetCache.has(prestacaoId)) {
              return documentoNumeroSetCache.get(prestacaoId)!;
            }
            if (!documentosFetchCache.has(prestacaoId)) {
              documentosFetchCache.set(prestacaoId, prestacoesApi.getDocumentos(prestacaoId));
            }
            const registros = await documentosFetchCache.get(prestacaoId)!;
            const numeroSet = new Set(
              registros
                .map((doc) => doc.numero_documento?.trim().toLowerCase())
                .filter((numero): numero is string => !!numero && numero.length > 0)
            );
            documentoNumeroSetCache.set(prestacaoId, numeroSet);
            return numeroSet;
          };

          const pickFromRow = (row: Record<string, any>, keys: string[]) => {
            for (const key of keys) {
              if (key in row && row[key] !== undefined && row[key] !== null) {
                const value = row[key];
                if (typeof value === 'string' && value.trim() === '') continue;
                return value;
              }
            }
            return null;
          };

          const sanitizeDigits = (value: any) => {
            if (value === null || value === undefined) return '';
            return String(value).replace(/\D+/g, '');
          };

          const parseDecimal = (value: any) => {
            if (value === null || value === undefined) return null;
            if (typeof value === 'number' && !isNaN(value)) return value;
            if (typeof value === 'string') {
              const hasComma = value.includes(',');
              const normalized = hasComma ? value.replace(/\./g, '').replace(',', '.') : value;
              const cleaned = normalized.replace(/[^0-9.-]/g, '');
              const parsed = parseFloat(cleaned);
              return isNaN(parsed) ? null : parsed;
            }
            return null;
          };

          const resolveDateValue = (value: any) => {
            if (!value && value !== 0) return null;
            if (value instanceof Date) return value;
            if (typeof value === 'number') {
              return new Date((value - 25569) * 86400 * 1000);
            }
            const parsed = new Date(value);
            return isNaN(parsed.getTime()) ? null : parsed;
          };

          const formatAsISODate = (date: Date) => {
            const year = date.getFullYear();
            const month = `${date.getMonth() + 1}`.padStart(2, '0');
            const day = `${date.getDate()}`.padStart(2, '0');
            return `${year}-${month}-${day}`;
          };

          const fornecedorNomeKeys = ['NOMEFORNECEDOR', 'FORNECEDOR', 'NOME CLIENTE', 'RAZAOSOCIAL', 'DESCRICAOFORNECEDOR', 'NOMEFANTASIA'];
          const fornecedorDocumentoKeys = ['CGCCFO', 'CNPJ', 'CPF/CNPJ', 'CGC', 'CPF_CNPJ', 'CNPJFORNECEDOR'];
          const numeroDocumentoKeys = ['NUMERODOCUMENTO', 'NUMDOCUMENTO', 'NUMERO DOCUMENTO', 'NOSSONUMERO', 'NRTITULO', 'NUMEROTITULO'];
          const rubricaKeys = ['NOMERUBRICA', 'RUBRICA', 'NATUREZA', 'DESCRICAORATEIO', 'DESCRICAO', 'TIPODESPESA', 'NOMEDESPESA'];
          const historicoKeys = ['HISTORICO', 'HISTORICO RATEIO', 'OBSERVACAO', 'MEMORIA', 'DESCRICAO DETALHADA'];
          const codigoGedKeys = ['NUM_FLUIG']
          const codigoMovimentacaoKeys = ['IDMOV']
          const codigoLancamentoKeys = ['IDLAN']

          let documentosCriados = 0;
          let documentosFalhos = 0;

          const documentoTasks = jsonData.map((item: Record<string, any>, index: number) => (async () => {
            const codigo = item[CODIGO_FILIAL];
            if (!codigo && codigo !== 0) return;

            const unidade = unidadesPorCodigo.get(String(codigo).trim());
            if (!unidade) return;

            const dataReferencia = resolveDataReferencia(item);
            if (!dataReferencia) return;

            const ano = dataReferencia.getFullYear();
            const mes = dataReferencia.getMonth() + 1;
            if (!ano || !mes) return;

            const prestacao = prestacoesPorChave.get(getPrestacaoKey(unidade.id, ano, mes));
            if (!prestacao) return;

            const valorOriginalParsed = parseDecimal(item[VALOR_ORIGINAL]);
            const valorLiquidoParsed = parseDecimal(item[VALOR_LIQUIDO]);
            const valorBase = valorLiquidoParsed ?? valorOriginalParsed;
            if (valorBase === null) return;

            const valorOriginalValue = valorOriginalParsed ?? valorBase;
            const valorLiquidoValue = valorLiquidoParsed ?? valorBase;

            const fornecedorNomeRaw = pickFromRow(item, fornecedorNomeKeys);
            const fornecedorNome =
              typeof fornecedorNomeRaw === 'string' && fornecedorNomeRaw.trim().length > 0
                ? fornecedorNomeRaw.trim()
                : unidade.nome || 'Fornecedor não informado';

            const fornecedorDocumentoDigits = sanitizeDigits(pickFromRow(item, fornecedorDocumentoKeys));
            const fornecedorDocumento = fornecedorDocumentoDigits || '00000000000000';

            const numeroDocumentoRaw = pickFromRow(item, numeroDocumentoKeys);
            const numeroDocumento =
              (typeof numeroDocumentoRaw === 'string' && numeroDocumentoRaw.trim().length > 0
                ? numeroDocumentoRaw.trim()
                : numeroDocumentoRaw) || `IMPORT-${prestacao.id}-${index + 1}`;

            const rubricaRaw = pickFromRow(item, rubricaKeys);
            const rubrica =
              typeof rubricaRaw === 'string' && rubricaRaw.trim().length > 0 ? rubricaRaw.trim() : 'Despesa não informada';

            const historicoRaw = pickFromRow(item, historicoKeys);
            const historico =
              typeof historicoRaw === 'string' && historicoRaw.trim().length > 0
                ? historicoRaw.trim()
                : 'Documento importado automaticamente via planilha';

            const dataEmissaoDate = resolveDateValue(item.DATAEMISSAO) || dataReferencia;
            const dataLancamentoDate = resolveDateValue(item.DATALANCAMENTO) || dataEmissaoDate || dataReferencia;

            const codigoGedRaw = pickFromRow(item, codigoGedKeys);
            const codigoGed = typeof codigoGedRaw === 'string' && codigoGedRaw.trim().length > 0 ? codigoGedRaw.trim() : '';

            const codigoMovimentacaoRaw = String(pickFromRow(item, codigoMovimentacaoKeys));
            const codigoMovimentacao = typeof codigoMovimentacaoRaw === 'string' && codigoMovimentacaoRaw.trim().length > 0 ? codigoMovimentacaoRaw.trim() : null;

            const codigoLancamentoRaw = String(pickFromRow(item, codigoLancamentoKeys));
            const codigoLancamento = typeof codigoLancamentoRaw === 'string' && codigoLancamentoRaw.trim().length > 0 ? codigoLancamentoRaw.trim() : null;

            let numeroSet: Set<string>;
            try {
              numeroSet = await ensureDocumentoNumeroSet(prestacao.id);
            } catch (error) {
              console.error('Erro ao carregar documentos existentes da prestação:', error);
              documentosFalhos += 1;
              return;
            }

            const numeroKey = String(numeroDocumento).trim().toLowerCase();
            if (numeroSet.has(numeroKey)) return;

            const documentoPayload: CriarDocumentoFiscalInput = {
              tipo_lancamento: 'rateio',
              tipo_documento: 'nota_fiscal',
              fornecedor_nome: fornecedorNome,
              fornecedor_documento: fornecedorDocumento,
              data_emissao: formatAsISODate(dataEmissaoDate),
              data_lancamento: formatAsISODate(dataLancamentoDate),
              mes_lancamento: mes,
              numero_documento: String(numeroDocumento),
              natureza_documento: rubrica,
              rubrica_documento: rubrica,
              valor_original: String(valorOriginalValue),
              valor_liquido: String(valorLiquidoValue),
              historico,
              importado: true,
              codigo_ged: codigoGed,
              codigo_movimentacao: codigoMovimentacao,
              codigo_lancamento: codigoLancamento
            };

            try {
              await prestacoesApi.addDocumento(prestacao.id, documentoPayload);
              numeroSet.add(numeroKey);
              documentosCriados += 1;
            } catch (error) {
              console.error('Erro ao importar documento da planilha:', error);
              documentosFalhos += 1;
            }
          })());

          await Promise.all(documentoTasks);

          if (documentosCriados > 0) {
            toast.success('Documentos importados', `${documentosCriados} documento(s) adicionados automaticamente.`);
            await loadData();
          } else if (!documentosFalhos) {
            toast.info('Nenhum documento importado', 'Os documentos da planilha já estavam na base ou faltam dados obrigatórios.');
          }

          if (documentosFalhos > 0) {
            toast.error('Alguns documentos falharam', `${documentosFalhos} documento(s) não foram importados. Verifique os dados e tente novamente.`);
          }
        };

        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error('Error reading XLSX:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <BackButton to="/dashboard" />
        </div>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Prestações de Contas
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Gerencie e acompanhe suas prestações de contas
            </p>
          </div>

          {/* Botões de ação */}
          <div className='flex gap-2'>
            {(isOperator() || isAdminRole()) && (
              <Link
                to="/prestacoes/nova"
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Nova Prestação</span>
              </Link>
            )}
            {(isOperator() || isAdminRole()) && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <Upload className="w-5 h-5" />
                <span>Importar XLSX</span>
              </button>
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ano
              </label>
              <select
                value={filtroAno}
                onChange={(e) => setFiltroAno(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {[2024, 2025, 2026].map((ano) => (
                  <option key={ano} value={ano}>{ano}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Unidade
              </label>
              <select
                value={filtroUnidade || ''}
                onChange={(e) => setFiltroUnidade(e.target.value ? Number(e.target.value) : undefined)}
                disabled={selectedUnidadeId !== null && selectedUnidadeId !== undefined}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Todas as unidades</option>
                {unidades.map((unidade) => (
                  <option key={unidade.id} value={unidade.id}>
                    {unidade.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Buscar
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Nome ou CPF do responsável..."
                  value={filtroBusca}
                  onChange={(e) => setFiltroBusca(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {prestacoes.filter(p => {
          if (!filtroBusca) return true;
          const busca = filtroBusca.toLowerCase();
          return (
            (p.responsavel_nome?.toLowerCase().includes(busca)) ||
            (p.responsavel_cpf?.toLowerCase().includes(busca))
          );
        }).length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Nenhuma prestação encontrada
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {filtroBusca ? 'Tente ajustar os filtros de busca' : 'Crie uma nova prestação de contas para começar'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {prestacoes.filter(p => {
              if (!filtroBusca) return true;
              const busca = filtroBusca.toLowerCase();
              return (
                (p.responsavel_nome?.toLowerCase().includes(busca)) ||
                (p.responsavel_cpf?.toLowerCase().includes(busca))
              );
            }).map((prestacao) => {
              const somaDeferidos = Number(prestacao.soma_valor_deferidos ?? 0);
              const somaPendentes = Number(prestacao.soma_valor_pendentes ?? 0);
              const somaIndeferidos = Number(prestacao.soma_valor_indeferidos ?? 0);
              const totalValorDocumentos = somaDeferidos + somaPendentes + somaIndeferidos;

              return (
                <Link
                key={prestacao.id}
                to={`/prestacoes/${prestacao.id}`}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <Building2 className="w-5 h-5 text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {prestacao?.unidade?.nome || 'Unidade sem nome'}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(prestacao.status)}`}>
                      {prestacao.status.charAt(0).toUpperCase() + prestacao.status.slice(1)}
                    </span>
                    {renderPrestacaoAprovacaoBadge(prestacao.status_aprovacao)}
                  </div>
                </div>
                <div className="flex items-center space-x-6 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>{getMesNome(prestacao.mes)} {prestacao.ano}</span>
                  </div>
                  {prestacao.responsavel_nome && (
                    <div>
                      <span className="font-medium">Responsável:</span> {prestacao.responsavel_nome}
                    </div>
                  )}
                </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {prestacao.total_documentos || 0}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Total de Documentos</p>
                      <p className="text-xs font-semibold text-gray-900 dark:text-white mt-1">
                        {formatCurrencyWithSymbol(totalValorDocumentos)}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-2xl font-bold">{prestacao.documentos_deferidos || 0}</span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Deferidos</p>
                      <p className="text-xs font-semibold text-gray-900 dark:text-white mt-1">
                        {formatCurrencyWithSymbol(somaDeferidos)}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center space-x-2 text-yellow-600 dark:text-yellow-400">
                        <Clock className="w-5 h-5" />
                        <span className="text-2xl font-bold">{prestacao.documentos_pendentes || 0}</span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Pendentes</p>
                      <p className="text-xs font-semibold text-gray-900 dark:text-white mt-1">
                        {formatCurrencyWithSymbol(somaPendentes)}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                        <XCircle className="w-5 h-5" />
                        <span className="text-2xl font-bold">{prestacao.documentos_indeferidos || 0}</span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Indeferidos</p>
                      <p className="text-xs font-semibold text-gray-900 dark:text-white mt-1">
                        {formatCurrencyWithSymbol(somaIndeferidos)}
                      </p>
                    </div>
                  </div>
                </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
